import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  auth,
  onAuthStateChanged,
  loginWithGoogle,
  logout,
  ensureUserDoc,
  consumeRedirectResult,
} from "../lib/firebase";

const AuthContext = createContext({
  user: null,
  role: "public",
  tags: [],
  loading: true,
  authError: null,
  signIn: async () => {},
  signOut: async () => {},
});

const ROLE_RANK = { public: 0, client: 1, staff: 2, admin: 3 };

export const hasRole = (current, required) =>
  (ROLE_RANK[current] ?? 0) >= (ROLE_RANK[required] ?? 0);

export const hasTag = (currentTags, tag) =>
  Array.isArray(currentTags) && currentTags.includes(tag);

const errCode = (err) => (err && err.code) ? err.code : (err ? String(err) : null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("public");
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined" && process.env.GATSBY_E2E === "1") {
      const stub = window.__DIOSCURO_E2E_USER__;
      if (stub) {
        setUser({
          uid: stub.uid,
          email: stub.email,
          displayName: stub.displayName || null,
          photoURL: stub.photoURL || null,
        });
        setRole(stub.role || "public");
        setTags(Array.isArray(stub.tags) ? stub.tags : []);
        setLoading(false);
        return () => {};
      }
    }
    if (!auth) {
      setLoading(false);
      return;
    }
    // Surface any errors returned by the redirect flow on this page load
    consumeRedirectResult()
      .then(() => setAuthError(null))
      .catch((err) => setAuthError(errCode(err)));
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        try {
          const userDoc = await ensureUserDoc(fbUser);
          setUser(fbUser);
          setRole((userDoc && userDoc.role) || "public");
          setTags(userDoc && Array.isArray(userDoc.tags) ? userDoc.tags : []);
          setAuthError(null);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[auth] ensureUserDoc failed:", errCode(err), err);
          setUser(fbUser);
          setRole("public");
          setTags([]);
          setAuthError(errCode(err));
        }
      } else {
        setUser(null);
        setRole("public");
        setTags([]);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const signIn = useCallback(async () => {
    setAuthError(null);
    try {
      await loginWithGoogle();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[auth] sign-in error:", errCode(err), err);
      setAuthError(errCode(err));
    }
  }, []);

  const signOut = useCallback(async () => {
    await logout();
  }, []);

  const refreshUser = useCallback(async () => {
    if (!auth || !auth.currentUser) return;
    try {
      await auth.currentUser.reload();
    } catch (_) {
      /* ignore */
    }
    // Build a new reference so React picks up the change — auth.currentUser
    // mutates in place, which doesn't trigger a re-render on its own.
    const fb = auth.currentUser;
    if (!fb) return;
    setUser({
      uid: fb.uid,
      email: fb.email,
      displayName: fb.displayName || null,
      photoURL: fb.photoURL || null,
      providerData: fb.providerData || [],
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, role, tags, loading, authError, signIn, signOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
